<!--
  Rates — the effective-dated price lists.

  Each tab lists its items with the CURRENT figure (the row in force today)
  and its effective-from date. "Change" adds a row; nothing is ever edited in
  place, so a project that spans a price change carries both prices. History
  expands the earlier rows, each deletable behind an inline confirmation —
  no confirm() dialogs anywhere in this app.

  Mix prices lists every mix design in the library, priced or not; there is
  no "add mix" here because the library is the mix design area's.

  Trucking is per tonne delivered and differs by where the job is, so a rate
  belongs to a job site; the site-less "Default" one covers every site without
  its own. A rate also names the materials it is charged on, because cement
  usually arrives with the haul already inside its tonne price.
  Materials and Activity rates work the same way: a price with no site is the
  default, and a site can carry its own that wins for that site only.
  Activity rates are what SCI BILLS for a Daily Report's Other activities —
  Standby by the hour, Delivery by the load, a Small load fee each.
  Sales tax is the two percentages every price in the app is entered
  net of — GST is recoverable and PST is not, which is the P&L's business, not
  this screen's.

  Look: the admin hero, a scrolling strip of tabs, then one card per priced
  thing. A card's head reads on its own — the name and the rate in force —
  because everything under it is folded away until asked for.
-->
<script lang="ts">
  import { api, isApiError } from '../../lib/api';
  import { DEFAULT_OTHER_ACTIVITIES, TRUCKED_MATERIALS, chargedMaterials, foldName } from '../../lib/calc/pnl';
  import { money } from '../../lib/money';
  import { linkHandler, routePath } from '../../lib/router';
  import { notify, notifyError, todayIso } from '../../lib/store';
  import type {
    ActivityRate,
    ActivityUnit,
    CancellationFee,
    EquipmentRate,
    MaterialPrice,
    MixDesignOption,
    MixDesignPrice,
    OperatorRate,
    RateGapsResponse,
    SiteSummary,
    TaxKind,
    TaxRate,
    TruckingRate,
    WorkerRate
  } from '../../lib/types';
  import AdminHero from './AdminHero.svelte';
  import './admin-look.css';

  type Tab =
    | 'materials'
    | 'activities'
    | 'workers'
    | 'mixes'
    | 'trucking'
    | 'taxes'
    | 'equipment'
    | 'operators'
    | 'cancellation';
  let tab = $state<Tab>('materials');

  let materials = $state<MaterialPrice[]>([]);
  let activityRates = $state<ActivityRate[]>([]);
  let workers = $state<WorkerRate[]>([]);
  let mixes = $state<{ mix_design: MixDesignOption; prices: MixDesignPrice[] }[]>([]);
  let truckingRates = $state<TruckingRate[]>([]);
  let taxRates = $state<TaxRate[]>([]);
  let equipmentRates = $state<EquipmentRate[]>([]);
  let operatorRates = $state<OperatorRate[]>([]);
  let cancellationFees = $state<CancellationFee[]>([]);
  let sites = $state<SiteSummary[]>([]);
  let loading = $state(true);

  /**
   * Names seen on a Daily Report that carry no rate. Only Materials and
   * Workers can have them — Mixes, Trucking and Sales tax are chosen from
   * inside the app, so a name there cannot arrive unpriced. Equipment and
   * operator names on a Contract Report are free text with a pick-list,
   * exactly like materials, so they CAN arrive unpriced too — but
   * `findRateGaps` does not extend to them yet (spec, "deliberately not
   * building"); the admin P&L already reports an unpriced contract line with
   * its reason (I2), which is the visibility that exists today.
   */
  let gaps = $state<RateGapsResponse>({
    workers: [],
    materials: [],
    dismissed: { workers: [], materials: [] }
  });

  const tabGaps = $derived(
    tab === 'workers' ? gaps.workers : tab === 'materials' ? gaps.materials : []
  );

  /** Names hidden from this tab's gap list, so they can be offered back. */
  const tabDismissed = $derived(
    tab === 'workers'
      ? gaps.dismissed.workers
      : tab === 'materials'
        ? gaps.dismissed.materials
        : []
  );

  /**
   * Stop asking for a rate for this name, or start again.
   *
   * The same promise the Remove button on a priced worker makes: the name
   * leaves this screen, and nothing that happened on a job changes.
   */
  async function setDismissed(name: string, dismissed: boolean) {
    if (tab !== 'workers' && tab !== 'materials') return;
    try {
      gaps = await api.dismissName({
        kind: tab === 'workers' ? 'worker' : 'material',
        name,
        dismissed
      });
      notify({ tone: 'ok', title: dismissed ? `Removed ${name}` : `Restored ${name}` });
    } catch (cause) {
      notifyError(cause, dismissed ? 'Could not remove that name' : 'Could not restore that name');
    }
  }

  async function load() {
    loading = true;
    try {
      const [m, w, x, t, g, s, gap, eq, op, cf, ar] = await Promise.all([
        api.listMaterialPrices(),
        api.listWorkerRates(),
        api.listMixPrices(),
        api.listTruckingRates(),
        api.listTaxRates(),
        api.listSites(),
        api.listRateGaps(),
        api.listEquipmentRates(),
        api.listOperatorRates(),
        api.listCancellationFees(),
        api.listActivityRates()
      ]);
      activityRates = ar.activity_rates;
      materials = m.material_prices;
      workers = w.worker_rates;
      mixes = x.mixes;
      truckingRates = t.trucking_rates;
      taxRates = g.tax_rates;
      sites = s.sites;
      gaps = gap;
      equipmentRates = eq.equipment_rates;
      operatorRates = op.operator_rates;
      cancellationFees = cf.cancellation_fees;
    } catch (cause) {
      notifyError(cause, 'Could not load the rates');
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });

  function today(): string {
    return todayIso();
  }

  /** Rows grouped by name, newest effective date first (the API's order). */
  interface Group<T> { key: string; label: string; rows: T[] }

  function groupRows<T extends { effective_from: string }>(rows: T[], label: (r: T) => string): Group<T>[] {
    const groups = new Map<string, Group<T>>();
    for (const row of rows) {
      const key = label(row).trim().replace(/\s+/g, ' ').toLowerCase();
      const g = groups.get(key);
      if (g) g.rows.push(row);
      else groups.set(key, { key, label: label(row), rows: [row] });
    }
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  /** The row in force today: greatest effective_from ≤ today. */
  function current<T extends { effective_from: string }>(rows: T[]): T | null {
    const day = today();
    let best: T | null = null;
    for (const r of rows) {
      if (r.effective_from > day) continue;
      if (!best || r.effective_from > best.effective_from) best = r;
    }
    return best;
  }

  /**
   * Materials and activities are priced per site: the site-less row is the
   * default, a site's own row wins for that site only. So a group is one
   * name in one price list, and the list it belongs to is said under the name.
   */
  interface SiteGroup<T> { key: string; label: string; siteId: number | null; rows: T[] }

  function siteGroupRows<T extends { site_id: number | null }>(rows: T[], name: (r: T) => string): SiteGroup<T>[] {
    const groups = new Map<string, SiteGroup<T>>();
    for (const row of rows) {
      const key = `${row.site_id ?? 'default'}:${name(row).trim().replace(/\s+/g, ' ').toLowerCase()}`;
      const g = groups.get(key);
      if (g) g.rows.push(row);
      else groups.set(key, { key, label: name(row), siteId: row.site_id ?? null, rows: [row] });
    }
    return [...groups.values()].sort(
      (a, b) =>
        a.label.localeCompare(b.label) ||
        (a.siteId === null ? -1 : b.siteId === null ? 1 : siteName(a.siteId).localeCompare(siteName(b.siteId)))
    );
  }

  function siteSub(siteId: number | null): string {
    return siteId === null ? 'All sites (default)' : `${siteName(siteId)} only`;
  }

  const materialGroups = $derived(siteGroupRows(materials.filter((r) => r.archived === 0), (r) => r.material));

  /**
   * Activity rates, plus a placeholder for each of the three standard
   * activities that has no default rate yet — so the screen says what is
   * still to set rather than starting blank.
   */
  const activityGroups = $derived.by(() => {
    const live = siteGroupRows(activityRates.filter((r) => r.archived === 0), (r) => r.activity);
    const removed = new Set(activityRates.filter((r) => r.archived === 1 && r.site_id === null).map((r) => foldName(r.activity)));
    const placeholders: SiteGroup<ActivityRate>[] = DEFAULT_OTHER_ACTIVITIES
      .filter((d) => !live.some((g) => g.siteId === null && foldName(g.label) === foldName(d.name)) && !removed.has(foldName(d.name)))
      .map((d) => ({ key: `default:${foldName(d.name)}`, label: d.name, siteId: null, rows: [] }));
    return [...live, ...placeholders].sort(
      (a, b) =>
        a.label.localeCompare(b.label) ||
        (a.siteId === null ? -1 : b.siteId === null ? 1 : siteName(a.siteId).localeCompare(siteName(b.siteId)))
    );
  });

  const UNIT_LABEL: Record<ActivityUnit, string> = { hour: 'per hour', load: 'per load', each: 'each' };

  function defaultUnitFor(name: string): ActivityUnit {
    return DEFAULT_OTHER_ACTIVITIES.find((d) => foldName(d.name) === foldName(name))?.unit ?? 'each';
  }
  /**
   * Workers split by whether they have been removed from the list. A removed
   * worker keeps every rate row — past days still price — and can be
   * restored; only a worker who is still on the crew is listed by default.
   */
  const workerGroups = $derived(groupRows(workers.filter((r) => r.archived === 0), (r) => r.name));

  /**
   * Equipment and operator charge-out rates, grouped by name the same way
   * materials are. Filtered by `archived === 0`, matching `workerGroups`
   * above (P2): there is no archive ROUTE for these two lists yet (only
   * list/create/delete), so the column is never set today and this filter is
   * a no-op in practice — but the row TYPE already carries `archived`
   * (matching `equipment_rate`/`operator_rate`'s schema), and the moment an
   * archive route ships, an archived rate must not silently reappear here.
   */
  const equipmentGroups = $derived(groupRows(equipmentRates.filter((r) => r.archived === 0), (r) => r.equipment));

  /**
   * The week/month figures a machine also carries, said under its name so the
   * card head reads whole while collapsed — "$500/wk · $6,000/mo", or nothing
   * when it is priced day-only. Only meaningful on the equipment tab; an
   * operator has no such rates, so this returns '' for it.
   */
  function equipmentExtras(now: EquipmentRate | OperatorRate | null): string {
    if (now === null || tab !== 'equipment') return '';
    const eq = now as EquipmentRate;
    const parts: string[] = [];
    if (eq.charge_per_week != null) parts.push(`${money(eq.charge_per_week)}/wk`);
    if (eq.charge_per_month != null) parts.push(`${money(eq.charge_per_month)}/mo`);
    return parts.join(' · ');
  }
  const operatorGroups = $derived(groupRows(operatorRates.filter((r) => r.archived === 0), (r) => r.name));

  /** The cancellation fee row in force today, or null before any is set. */
  const cancellationNow = $derived(current(cancellationFees));

  const DEFAULT_SITE_LABEL = 'Default (any site)';

  function siteName(id: number): string {
    return sites.find((s) => s.id === id)?.name ?? `Site #${id}`;
  }

  interface TruckingGroup { key: string; label: string; siteId: number | null; rows: TruckingRate[] }

  /**
   * The default group is always listed, priced or not: a site with no rate of
   * its own falls back to it, so its absence is the thing worth seeing.
   */
  const truckingGroups = $derived.by(() => {
    const groups = new Map<string, TruckingGroup>([
      ['truck:default', { key: 'truck:default', label: DEFAULT_SITE_LABEL, siteId: null, rows: [] }]
    ]);
    for (const row of truckingRates.filter((r) => r.archived === 0)) {
      const key = row.site_id === null ? 'truck:default' : `truck:${row.site_id}`;
      const group = groups.get(key);
      if (group) group.rows.push(row);
      else groups.set(key, { key, label: siteName(row.site_id as number), siteId: row.site_id, rows: [row] });
    }
    const [fallback, ...attached] = [...groups.values()];
    return [fallback, ...attached.sort((a, b) => a.label.localeCompare(b.label))];
  });

  /**
   * What a rate is charged on, said out loud only when it is not everything
   * freight can be charged on — so a card head that says nothing means the
   * ordinary case, and a narrowed rate reads correctly while collapsed.
   */
  function chargedOnLabel(rate: TruckingRate | null): string {
    const chosen = chargedMaterials(rate);
    if (chosen === null) return '';
    const named = TRUCKED_MATERIALS.filter((m) => chosen.has(foldName(m)));
    if (named.length === 0 || named.length === TRUCKED_MATERIALS.length) return '';
    return `Charged on ${named.join(', ')} only`;
  }

  interface TaxGroup { key: string; label: string; kind: TaxKind; rows: TaxRate[] }

  const TAX_KINDS: { kind: TaxKind; label: string }[] = [
    { kind: 'gst', label: 'GST' },
    { kind: 'pst', label: 'PST' }
  ];

  const taxGroups = $derived<TaxGroup[]>(
    TAX_KINDS.map(({ kind, label }) => ({
      key: `tax:${kind}`,
      label,
      kind,
      rows: taxRates.filter((r) => r.kind === kind)
    }))
  );

  /**
   * Mix designs with an active (non-archived) price series. A removed mix's
   * price rows stay — every price row for a mix_design_id is archived
   * together, never some and not others — so a removed mix simply stops
   * appearing here; see `removedMixes` below for where it goes instead.
   */
  const mixGroups = $derived(mixes.filter((m) => !m.prices.some((p) => p.archived === 1)));

  /**
   * A removed item's row, shaped the same regardless of which of the six
   * tabs it came from, so one fold and one History renderer can serve all
   * six. `mixDesignId`/`siteId` carry whatever `setArchived` needs to
   * restore that particular kind; a name-keyed kind needs neither.
   */
  interface RemovedGroup {
    key: string;
    label: string;
    rows: { id: number; effective_from: string; note: string }[];
    mixDesignId?: number;
    siteId?: number | null;
  }

  const removedMaterials = $derived<RemovedGroup[]>(
    siteGroupRows(materials.filter((r) => r.archived === 1), (r) => r.material)
  );
  const removedActivities = $derived<RemovedGroup[]>(
    siteGroupRows(activityRates.filter((r) => r.archived === 1), (r) => r.activity)
  );
  /**
   * Workers split by whether they have been removed from the list. A removed
   * worker keeps every rate row — past days still price — and can be
   * restored; only a worker who is still on the crew is listed by default.
   */
  const removedWorkers = $derived<RemovedGroup[]>(
    groupRows(workers.filter((r) => r.archived === 1), (r) => r.name)
  );
  const removedEquipment = $derived<RemovedGroup[]>(
    groupRows(equipmentRates.filter((r) => r.archived === 1), (r) => r.equipment)
  );
  const removedOperators = $derived<RemovedGroup[]>(
    groupRows(operatorRates.filter((r) => r.archived === 1), (r) => r.name)
  );
  const removedMixes = $derived<RemovedGroup[]>(
    mixes
      .filter((m) => m.prices.some((p) => p.archived === 1))
      .map((m) => ({
        key: `mix:${m.mix_design.id}`,
        label: m.mix_design.mix_code,
        rows: m.prices,
        mixDesignId: m.mix_design.id
      }))
  );
  const removedTrucking = $derived.by<RemovedGroup[]>(() => {
    const groups = new Map<string, RemovedGroup>();
    for (const row of truckingRates.filter((r) => r.archived === 1)) {
      const key = row.site_id === null ? 'truck:default' : `truck:${row.site_id}`;
      const group = groups.get(key);
      if (group) group.rows.push(row);
      else
        groups.set(key, {
          key,
          label: row.site_id === null ? DEFAULT_SITE_LABEL : siteName(row.site_id),
          rows: [row],
          siteId: row.site_id
        });
    }
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  });

  /** The current tab's removed list — one fold serves all six tabs. */
  const removedGroups = $derived<RemovedGroup[]>(
    tab === 'materials'
      ? removedMaterials
      : tab === 'activities'
        ? removedActivities
        : tab === 'workers'
        ? removedWorkers
        : tab === 'mixes'
          ? removedMixes
          : tab === 'trucking'
            ? removedTrucking
            : tab === 'equipment'
              ? removedEquipment
              : tab === 'operators'
                ? removedOperators
                : []
  );

  let showRemoved = $state(false);
  /** The group whose Remove is awaiting its inline Yes/No. Not prefixed by
   *  tab: switching tabs always calls `closeForm`, which clears it. */
  let confirmRemove = $state<string | null>(null);

  /**
   * Remove (or restore) an item from whichever of the six tabs is open.
   * Dispatches to the archive route for the current tab, keyed the way that
   * kind's rows are keyed — name for materials/workers/equipment/operators,
   * `mix_design_id` for mixes, `site_id` for trucking (null targets the
   * default rate). Only the `archived` flag flips; every rate row stays, so
   * History and every past day's pricing are unchanged.
   */
  async function setArchived(
    name: string,
    archived: boolean,
    extra: { mixDesignId?: number; siteId?: number | null } = {}
  ) {
    try {
      if (tab === 'materials') {
        const list = await api.archiveMaterial({ material: name, site_id: extra.siteId ?? null, archived });
        materials = list.material_prices;
      } else if (tab === 'activities') {
        const list = await api.archiveActivity({ activity: name, site_id: extra.siteId ?? null, archived });
        activityRates = list.activity_rates;
      } else if (tab === 'workers') {
        const list = await api.archiveWorker({ name, archived });
        workers = list.worker_rates;
      } else if (tab === 'mixes' && extra.mixDesignId !== undefined) {
        const list = await api.archiveMix({ mix_design_id: extra.mixDesignId, archived });
        mixes = list.mixes;
      } else if (tab === 'trucking') {
        const list = await api.archiveTrucking({ site_id: extra.siteId ?? null, archived });
        truckingRates = list.trucking_rates;
      } else if (tab === 'equipment') {
        const list = await api.archiveEquipment({ equipment: name, archived });
        equipmentRates = list.equipment_rates;
      } else if (tab === 'operators') {
        const list = await api.archiveOperator({ name, archived });
        operatorRates = list.operator_rates;
      } else {
        return;
      }
      closeForm();
      notify({ tone: 'ok', title: archived ? `${name} removed from the list` : `${name} restored` });
    } catch (cause) {
      notifyError(cause, archived ? `Could not remove ${name}` : `Could not restore ${name}`);
    }
  }

  /** The figure to show for a removed row in History — same shape as the
   *  live-list figure formatters, just picked by tab instead of by type. */
  function removedFigureOf(row: {
    cost_per_tonne?: number;
    hourly_rate?: number;
    charge_per_day?: number;
    sell_per_m3?: number;
    rate?: number;
  }): string {
    if (tab === 'materials' || tab === 'trucking') return money(row.cost_per_tonne ?? 0);
    if (tab === 'activities') return money(row.rate ?? 0);
    if (tab === 'workers') return money(row.hourly_rate ?? 0);
    if (tab === 'equipment' || tab === 'operators') return money(row.charge_per_day ?? 0);
    if (tab === 'mixes') return money(row.sell_per_m3 ?? 0);
    return '';
  }

  // --- the one inline form, keyed by what it is for -------------------------

  /** Which group's Change form (or which tab's Add form) is open. */
  let openForm = $state<string | null>(null);
  let formName = $state('');
  let formFigure = $state('');
  /** Equipment only: the optional per-week and per-month figures. Blank = not offered. */
  let formFigureWeek = $state('');
  let formFigureMonth = $state('');
  let formDate = $state('');
  let formNote = $state('');
  /** '' is the default trucking rate — the one with no site. */
  let formSite = $state('');
  /** The site of the card whose Change form is open, so picking another site can say what it will do. */
  let formCardSite = $state('');
  /** Which materials a new trucking rate is charged on; all of them to start. */
  let formMaterials = $state<string[]>([...TRUCKED_MATERIALS]);
  let formKind = $state<TaxKind>('gst');
  /** Activity rates only: what one of "how many" is. */
  let formUnit = $state<ActivityUnit>('each');
  let formError = $state<string | null>(null);
  let saving = $state(false);
  let expanded = $state<Record<string, boolean>>({});

  /** History's expand/collapse state is prefixed by tab too, for the same
   *  reason (a material and a worker can share a folded name) — prefixed
   *  rather than cleared on tab switch, so History stays open on a round trip. */
  function expandedKey(key: string): string {
    return `${tab}:${key}`;
  }

  function openChange(key: string, name: string, site: number | null = null, kind: TaxKind = 'gst') {
    openForm = key;
    formName = name;
    formFigure = '';
    formFigureWeek = '';
    formFigureMonth = '';
    formDate = today();
    formNote = '';
    formError = null;
    formSite = site === null ? '' : String(site);
    formCardSite = formSite;
    formKind = kind;
    formMaterials = [...TRUCKED_MATERIALS];
    formUnit = defaultUnitFor(name);
  }

  function openAdd() {
    openChange(`add:${tab}`, '');
  }

  function closeForm() {
    openForm = null;
    confirmRemove = null;
  }

  /**
   * A blank optional figure is `null` ("not offered"); a non-blank one must
   * parse to a number ≥ 0, else `INVALID`. Kept distinct from the required
   * `formFigure` path, which treats blank as an error rather than a null.
   */
  const INVALID = Symbol('invalid-figure');
  function optionalFigure(raw: string): number | null | typeof INVALID {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? n : INVALID;
  }

  async function submit(mixDesignId: number | null = null) {
    if (saving) return;
    saving = true;
    formError = null;
    const trimmed = formFigure.trim();
    const figure = trimmed === '' ? NaN : Number(trimmed);
    if (!Number.isFinite(figure) || figure < 0) {
      formError = 'Enter a figure of 0 or more.';
      saving = false;
      return;
    }
    // A rate charged on nothing would never bill a load; it would just look
    // set from the card head.
    if (tab === 'trucking' && formMaterials.length === 0) {
      formError = 'Tick at least one material this rate is charged on.';
      saving = false;
      return;
    }
    try {
      if (tab === 'materials') {
        await api.createMaterialPrice({
          material: formName,
          site_id: formSite === '' ? null : Number(formSite),
          cost_per_tonne: figure, effective_from: formDate, note: formNote
        });
      } else if (tab === 'activities') {
        await api.createActivityRate({
          activity: formName,
          unit: formUnit,
          site_id: formSite === '' ? null : Number(formSite),
          rate: figure, effective_from: formDate, note: formNote
        });
      } else if (tab === 'workers') {
        await api.createWorkerRate({ name: formName, hourly_rate: figure, effective_from: formDate, note: formNote });
      } else if (tab === 'trucking') {
        await api.createTruckingRate({
          site_id: formSite === '' ? null : Number(formSite),
          materials: formMaterials,
          cost_per_tonne: figure, effective_from: formDate, note: formNote
        });
      } else if (tab === 'taxes') {
        await api.createTaxRate({ kind: formKind, percent: figure, effective_from: formDate, note: formNote });
      } else if (tab === 'equipment') {
        // Optional: a blank week/month box means "not offered" (null); a
        // non-blank one must be a real figure of 0 or more. The server holds
        // the same guard, but catching it here keeps the inline error close to
        // the box the owner typed in.
        const week = optionalFigure(formFigureWeek);
        const month = optionalFigure(formFigureMonth);
        if (week === INVALID || month === INVALID) {
          formError = 'A weekly or monthly rate must be a figure of 0 or more, or left blank.';
          saving = false;
          return;
        }
        await api.createEquipmentRate({
          equipment: formName, charge_per_day: figure, charge_per_week: week, charge_per_month: month,
          effective_from: formDate, note: formNote
        });
      } else if (tab === 'operators') {
        await api.createOperatorRate({ name: formName, charge_per_day: figure, effective_from: formDate, note: formNote });
      } else if (tab === 'cancellation') {
        await api.createCancellationFee({ amount: figure, effective_from: formDate, note: formNote });
      } else if (mixDesignId !== null) {
        await api.createMixPrice({ mix_design_id: mixDesignId, sell_per_m3: figure, effective_from: formDate, note: formNote });
      }
      notify({ tone: 'ok', title: 'Rate saved' });
      closeForm();
      await load();
    } catch (cause) {
      formError = isApiError(cause)
        ? Object.values(cause.fields ?? {}).join('; ') || cause.message
        : 'Could not save. Check the connection and try again.';
    } finally {
      saving = false;
    }
  }

  const figureLabel = $derived(
    tab === 'activities'
      ? `$ ${UNIT_LABEL[formUnit]}`
      : tab === 'materials' || tab === 'trucking'
      ? '$ per tonne'
      : tab === 'workers'
        ? '$ per hour'
        : tab === 'equipment' || tab === 'operators'
          ? '$ per day'
          : tab === 'taxes'
            ? '% of the ex-tax price'
            : tab === 'cancellation'
              ? '$ flat fee'
              : '$ per m³'
  );

  const TABS: { id: Tab; label: string }[] = [
    { id: 'materials', label: 'Materials' },
    { id: 'activities', label: 'Activity rates' },
    { id: 'workers', label: 'Workers' },
    { id: 'mixes', label: 'Mix prices' },
    { id: 'trucking', label: 'Trucking' },
    { id: 'taxes', label: 'Sales tax' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'operators', label: 'Operators' },
    { id: 'cancellation', label: 'Cancellation fee' }
  ];
</script>

<AdminHero kicker="Admin · Performance" title="Rates">
  {#snippet links()}
    <a class="a-chip" href={routePath.admin()} onclick={linkHandler}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 12H5m7-7-7 7 7 7" /></svg>
      Dashboard
    </a>
  {/snippet}
</AdminHero>

<p class="note">A change is a new row from a date. Days before that date keep the old rate.</p>

<div class="tabs" role="tablist">
  {#each TABS as t (t.id)}
    {@const n = t.id === 'workers' ? gaps.workers.length : t.id === 'materials' ? gaps.materials.length : 0}
    <button class="tab" type="button" aria-pressed={tab === t.id} onclick={() => { tab = t.id; closeForm(); }}>
      {t.label}
      {#if n > 0}
        <span class="tab__count" title="{n} with no rate entered">{n}</span>
      {/if}
    </button>
  {/each}
</div>

{#snippet rateForm(mixDesignId: number | null, showName: boolean, showSite = false, removeKey: string | null = null)}
  <div class="rateform">
    {#if showName}
      <label class="field">
        <span class="label">
          {tab === 'materials'
            ? 'Material'
            : tab === 'activities'
              ? 'Activity (as on the Daily Report)'
              : tab === 'equipment'
              ? 'Equipment'
              : tab === 'operators'
                ? 'Operator name'
                : 'Name (as on the Daily Report)'}
        </span>
        <input type="text" bind:value={formName} />
      </label>
    {/if}
    {#if showSite}
      <label class="field">
        <span class="label">Applies to</span>
        <select bind:value={formSite}>
          <option value="">{DEFAULT_SITE_LABEL}</option>
          {#each sites as site (site.id)}
            <option value={String(site.id)}>{site.name}{site.city ? ` — ${site.city}` : ''}</option>
          {/each}
        </select>
        {#if !showName && formSite !== formCardSite}
          <span class="field__hint">
            {formSite === ''
              ? 'Saves a default price for every site; this card keeps its own.'
              : `Saves a separate price for ${siteName(Number(formSite))} only — this card stays as it is.`}
          </span>
        {/if}
      </label>
    {/if}
    {#if tab === 'activities'}
      <label class="field">
        <span class="label">Counted</span>
        <select bind:value={formUnit}>
          <option value="hour">Per hour</option>
          <option value="load">Per load</option>
          <option value="each">Each</option>
        </select>
      </label>
    {/if}
    {#if tab === 'trucking'}
      <div class="field">
        <span class="label">Charged on</span>
        <div class="checks">
          {#each TRUCKED_MATERIALS as material (material)}
            <label class="check">
              <input type="checkbox" value={material} bind:group={formMaterials} />
              {material}
            </label>
          {/each}
        </div>
        <p class="field__hint">Cement is usually quoted delivered, with the haul already in its price — untick it rather than pay freight on it twice.</p>
      </div>
    {/if}
    <label class="field">
      <span class="label">{figureLabel}</span>
      <input type="text" inputmode="decimal" bind:value={formFigure} />
    </label>
    {#if tab === 'equipment'}
      <!--
        The two optional longer-span rates. Left blank, a machine is priced
        day-only and a Contract Report row set to Week or Month is flagged
        unpriced rather than billed at zero. The crew chooses the unit per row;
        this is only where the figures live.
      -->
      <label class="field">
        <span class="label">$ per week <small class="opt">(optional)</small></span>
        <input type="text" inputmode="decimal" bind:value={formFigureWeek} placeholder="not offered" />
      </label>
      <label class="field">
        <span class="label">$ per month <small class="opt">(optional)</small></span>
        <input type="text" inputmode="decimal" bind:value={formFigureMonth} placeholder="not offered" />
      </label>
    {/if}
    <label class="field">
      <span class="label">Effective from</span>
      <input type="date" bind:value={formDate} />
    </label>
    <label class="field">
      <span class="label">Note</span>
      <input type="text" bind:value={formNote} placeholder="e.g. H2 2026 contract" />
    </label>
    {#if formError}<p class="field__flag">{formError}</p>{/if}
    <div class="rateform__btns">
      <button class="a-cta" type="button" disabled={saving} onclick={() => void submit(mixDesignId)}>Save</button>
      <button class="btn" type="button" onclick={closeForm}>Cancel</button>
      {#if removeKey}
        <!--
          The owner asked for it here specifically: "when going into change
          there should be a delete button." Same word, same promise as every
          other Remove on this screen — it opens the same inline Yes/No
          below rather than a second dialog.
        -->
        <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmRemove = removeKey)}>Remove</button>
      {/if}
    </div>
  </div>
{/snippet}

<!--
  The head of a card, so it still says what it needs to with everything under
  it folded away: what this is, and the figure in force today.
-->
{#snippet rateHead(label: string, sub: string, figure: string | null, when: string, none: string)}
  <div class="rate__head">
    <div class="rate__name">
      {label}
      {#if sub}<span class="rate__sub">{sub}</span>{/if}
    </div>
    <div class="rate__now">
      {#if figure !== null}
        <b class="a-num">{figure}</b>
        <small>{when}</small>
      {:else}
        <span class="rate__none">{none}</span>
      {/if}
    </div>
  </div>
{/snippet}

{#snippet histToggle(key: string, count: number)}
  <button class="fold" type="button" aria-expanded={!!expanded[key]} onclick={() => (expanded[key] = !expanded[key])}>
    <svg viewBox="0 0 24 24" aria-hidden="true" class:open={expanded[key]}><path d="m6 9 6 6 6-6" /></svg>
    {expanded[key] ? 'Hide' : 'History'} ({count})
  </button>
{/snippet}

<!--
  Remove's inline Yes/No, shared by every card's card-head trigger (Workers
  only) and every card's Change-form trigger (all six). One key, one
  confirmation, no matter which button opened it.
-->
{#snippet removeAsk(key: string, label: string, mixDesignId?: number, siteId?: number | null)}
  {#if confirmRemove === key}
    <div class="ask ask--danger">
      <p>Remove {label} from the list? Its rates stay, so past days still price.</p>
      <div class="ask__btns">
        <button
          class="btn btn--sm btn--danger"
          type="button"
          onclick={() => void setArchived(label, true, { mixDesignId, siteId })}
        >
          Yes, remove
        </button>
        <button class="btn btn--sm" type="button" onclick={() => (confirmRemove = null)}>No</button>
      </div>
    </div>
  {/if}
{/snippet}

{#snippet history(rows: { id: number; effective_from: string; note: string }[], figureOf: (row: never) => string, groupKey: string)}
  {#if expanded[groupKey]}
    <!--
      Read-only, on purpose: a rate row is what actually priced a past day,
      so there is no per-row Delete here — nothing "never change recorded
      history" would allow it to do. Remove (on the card/Change form) is the
      only way to retire a name, and it archives forward without touching
      any row here.
    -->
    <ul class="hist">
      {#each rows as row (row.id)}
        <li>
          <div class="hist__row">
            <span class="mono hist__when">{row.effective_from}</span>
            <span class="a-num hist__figure">{figureOf(row as never)}</span>
            <span class="hist__note">{row.note}</span>
          </div>
        </li>
      {/each}
    </ul>
  {/if}
{/snippet}

{#if loading}
  <p class="meta center"><span class="spinner" aria-hidden="true"></span> Loading…</p>
{:else if tab === 'materials' || tab === 'workers'}
  <ul class="a-list">
    <!--
      What has no rate comes first: this list is a to-do before it is a
      reference, and a name with no dollar amount is the only thing on the
      screen that needs a decision.
    -->
    {#each tabGaps as gap, i (gap.name)}
      <li style="--i: {i}">
        <div class="a-card rate rate--gap">
          <span class="a-card__rail a-card__rail--muted" aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(gap.name, '', null, '', 'No rate set')}
            <p class="rate__seen">
              Seen on {gap.report_count} Daily Report{gap.report_count === 1 ? '' : 's'}, last {gap.last_seen}.
            </p>
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openChange(`add:${tab}`, gap.name)}>
                Set rate
              </button>
              <!--
                Labelled Remove, not Delete, because it makes the same promise
                as the Remove on a priced worker: off this screen, history
                untouched. A different word would imply it destroys more.
              -->
              <button class="btn btn--sm btn--ghost" type="button" onclick={() => void setDismissed(gap.name, true)}>
                Remove
              </button>
            </div>
          </div>
        </div>
      </li>
    {/each}
    {#each tab === 'materials' ? materialGroups : workerGroups as group, i (group.key)}
      {@const now = current<MaterialPrice | WorkerRate>(group.rows)}
      {@const groupSite = tab === 'materials' ? (group as SiteGroup<MaterialPrice>).siteId : null}
      <li style="--i: {i}">
        <div class="a-card rate">
          <span class="a-card__rail" class:a-card__rail--muted={now === null} aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(
              group.label,
              tab === 'materials' ? siteSub(groupSite) : '',
              now ? money(tab === 'materials' ? (now as MaterialPrice).cost_per_tonne : (now as WorkerRate).hourly_rate) : null,
              now ? `from ${now.effective_from}` : '',
              'no rate in force yet'
            )}
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openChange(group.key, group.label, groupSite)}>Change</button>
              {@render histToggle(expandedKey(group.key), group.rows.length)}
              {#if tab === 'workers' && confirmRemove !== group.key}
                <button class="btn btn--sm btn--ghost" type="button" onclick={() => (confirmRemove = group.key)}>Remove</button>
              {/if}
            </div>
            {#if openForm === group.key}{@render rateForm(null, false, tab === 'materials', group.key)}{/if}
            {@render removeAsk(group.key, group.label, undefined, groupSite)}
            {@render history(
              group.rows,
              tab === 'materials'
                ? ((r: MaterialPrice) => money(r.cost_per_tonne)) as never
                : ((r: WorkerRate) => money(r.hourly_rate)) as never,
              expandedKey(group.key)
            )}
          </div>
        </div>
      </li>
    {/each}
  </ul>

  {#if openForm === `add:${tab}`}
    <div class="addpanel">{@render rateForm(null, true, tab === 'materials')}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={openAdd}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add {tab === 'materials' ? 'material' : 'worker'}
    </button>
  {/if}
  {#if tab === 'materials'}
    <p class="note">A price with no site is the default for every site. Add the same material for one site to give that site its own price.</p>
  {/if}
{:else if tab === 'activities'}
  <p class="note">
    What SCI bills for a Daily Report's Other activities. The crew picks the activity and enters how many; the P&amp;L
    multiplies by the rate here. A rate with no site is the default; a site's own rate wins for that site.
  </p>
  <ul class="a-list">
    {#each activityGroups as group, i (group.key)}
      {@const now = current(group.rows)}
      <li style="--i: {i}">
        <div class="a-card rate" class:rate--gap={group.rows.length === 0}>
          <span class="a-card__rail" class:a-card__rail--muted={now === null} aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(
              group.label,
              siteSub(group.siteId),
              now ? money(now.rate) : null,
              now ? `${UNIT_LABEL[now.unit]}, from ${now.effective_from}` : '',
              group.rows.length === 0 ? 'No rate set' : 'no rate in force yet'
            )}
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => { openChange(group.key, group.label, group.siteId); if (now) formUnit = now.unit; }}>
                {group.rows.length === 0 ? 'Set rate' : 'Change'}
              </button>
              {#if group.rows.length > 0}
                {@render histToggle(expandedKey(group.key), group.rows.length)}
              {/if}
            </div>
            {#if openForm === group.key}{@render rateForm(null, false, true, group.rows.length > 0 ? group.key : null)}{/if}
            {@render removeAsk(group.key, group.label, undefined, group.siteId)}
            {@render history(group.rows, ((r: ActivityRate) => `${money(r.rate)} ${UNIT_LABEL[r.unit]}`) as never, expandedKey(group.key))}
          </div>
        </div>
      </li>
    {/each}
  </ul>

  {#if openForm === `add:${tab}`}
    <div class="addpanel">{@render rateForm(null, true, true)}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={openAdd}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add an activity rate
    </button>
  {/if}
{:else if tab === 'trucking'}
  <p class="note">
    Charged by the tonne on the materials each rate names. A site without a rate of its own is trucked at the default.
  </p>
  <ul class="a-list">
    {#each truckingGroups as group, i (group.key)}
      {@const now = current(group.rows)}
      <li style="--i: {i}">
        <div class="a-card rate">
          <span class="a-card__rail" class:a-card__rail--muted={now === null} aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(
              group.label,
              chargedOnLabel(now),
              now ? money(now.cost_per_tonne) : null,
              now ? `per tonne, from ${now.effective_from}` : '',
              'no rate in force yet'
            )}
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openChange(group.key, group.label, group.siteId)}>{now ? 'Change' : 'Set price'}</button>
              {#if group.rows.length > 0}
                {@render histToggle(expandedKey(group.key), group.rows.length)}
              {/if}
            </div>
            {#if openForm === group.key}{@render rateForm(null, false, false, group.key)}{/if}
            {@render removeAsk(group.key, group.label, undefined, group.siteId)}
            {@render history(group.rows, ((r: TruckingRate) => money(r.cost_per_tonne)) as never, expandedKey(group.key))}
          </div>
        </div>
      </li>
    {/each}
  </ul>

  {#if openForm === `add:${tab}`}
    <div class="addpanel">{@render rateForm(null, false, true)}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={openAdd}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add a rate for a site
    </button>
  {/if}
{:else if tab === 'taxes'}
  <p class="note">
    Every price in this app is entered ex-tax. GST is recoverable, so it is neither revenue nor cost — the P&amp;L
    tracks what is collected and what is paid. PST is not recoverable, so it is a real cost of the materials.
  </p>
  <!--
    No Remove here, and no per-row Delete either: GST and PST are two fixed
    kinds that always exist, so there is nothing to retire from a list. A
    wrong percentage is superseded by a new dated row, same as everywhere
    else — which means a tax rate row, once entered, cannot be taken away at
    all. That is correct under "never change recorded history", not a
    missing feature.
  -->
  <ul class="a-list">
    {#each taxGroups as group, i (group.key)}
      {@const now = current(group.rows)}
      <li style="--i: {i}">
        <div class="a-card rate">
          <span class="a-card__rail" class:a-card__rail--muted={now === null} aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(
              group.label,
              '',
              now ? `${now.percent}%` : null,
              now ? `from ${now.effective_from}` : '',
              'not charged'
            )}
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openChange(group.key, group.label, null, group.kind)}>{now ? 'Change' : 'Set rate'}</button>
              {#if group.rows.length > 0}
                {@render histToggle(expandedKey(group.key), group.rows.length)}
              {/if}
            </div>
            {#if openForm === group.key}{@render rateForm(null, false)}{/if}
            {@render history(group.rows, ((r: TaxRate) => `${r.percent}%`) as never, expandedKey(group.key))}
          </div>
        </div>
      </li>
    {/each}
  </ul>
{:else if tab === 'equipment' || tab === 'operators'}
  {#if tab === 'operators'}
    <!--
      The one warning on this screen. Both this tab and Workers price the same
      person, and both units are said out loud on purpose: "$48" alone is just
      a number, but "$48 per day" reads as wrong the moment an hourly wage
      lands in it — the mismatch is what actually catches the mistake, not
      just knowing the two tabs differ.
    -->
    <p class="note note--warn">
      <strong>What SCI bills for this person per day — not what they are paid.</strong>
      Their hourly wage lives on the
      <button class="link" type="button" onclick={() => { tab = 'workers'; closeForm(); }}>Workers tab</button>.
    </p>
  {/if}
  <ul class="a-list">
    {#each (tab === 'equipment' ? equipmentGroups : operatorGroups) as group, i (group.key)}
      {@const now = current<EquipmentRate | OperatorRate>(group.rows)}
      <li style="--i: {i}">
        <div class="a-card rate">
          <span class="a-card__rail" class:a-card__rail--muted={now === null} aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(
              group.label,
              equipmentExtras(now),
              now ? money(now.charge_per_day) : null,
              now ? `per day, from ${now.effective_from}` : '',
              'no rate in force yet'
            )}
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openChange(group.key, group.label)}>Change</button>
              {@render histToggle(expandedKey(group.key), group.rows.length)}
            </div>
            {#if openForm === group.key}{@render rateForm(null, false, false, group.key)}{/if}
            {@render removeAsk(group.key, group.label)}
            {@render history(
              group.rows,
              ((r: EquipmentRate | OperatorRate) => money(r.charge_per_day)) as never,
              expandedKey(group.key)
            )}
          </div>
        </div>
      </li>
    {/each}
  </ul>

  {#if openForm === `add:${tab}`}
    <div class="addpanel">{@render rateForm(null, true)}</div>
  {:else}
    <button class="a-cta add" type="button" onclick={openAdd}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Add {tab === 'equipment' ? 'equipment' : 'operator'}
    </button>
  {/if}
{:else if tab === 'cancellation'}
  <!--
    No name to group by — it is one flat figure over time, so it reads as a
    single card (like a tax kind) rather than a list of named things. Forcing
    it through groupRows would invent a name for something that has none.
  -->
  <p class="note">
    One flat fee, billed per cancelled contract day — not tied to a name. A change is a new dated row, same as every
    other rate here.
  </p>
  <!--
    No Remove here, and no per-row Delete either: there is only ever one
    cancellation fee to price with, nothing to retire from a list. A wrong
    amount is superseded by a new dated row, which means a cancellation-fee
    row, once entered, cannot be taken away at all. That is correct under
    "never change recorded history", not a missing feature.
  -->
  <ul class="a-list">
    <li>
      <div class="a-card rate">
        <span class="a-card__rail" class:a-card__rail--muted={cancellationNow === null} aria-hidden="true"></span>
        <div class="rate__body">
          {@render rateHead(
            'Cancellation fee',
            '',
            cancellationNow ? money(cancellationNow.amount) : null,
            cancellationNow ? `from ${cancellationNow.effective_from}` : '',
            'not set'
          )}
          <div class="acts">
            <button class="btn btn--sm" type="button" onclick={() => openChange('cancellation', 'Cancellation fee')}>
              {cancellationNow ? 'Change' : 'Set fee'}
            </button>
            {#if cancellationFees.length > 0}
              {@render histToggle(expandedKey('cancellation'), cancellationFees.length)}
            {/if}
          </div>
          {#if openForm === 'cancellation'}{@render rateForm(null, false)}{/if}
          {@render history(
            cancellationFees,
            ((r: CancellationFee) => money(r.amount)) as never,
            expandedKey('cancellation')
          )}
        </div>
      </div>
    </li>
  </ul>
{:else}
  {#if mixes.length === 0}
    <div class="empty">
      <strong>No mix designs yet</strong>
      <span class="meta">A mix shows up here as soon as one exists in the mix design library.</span>
    </div>
  {/if}
  <ul class="a-list">
    {#each mixGroups as entry, i (entry.mix_design.id)}
      {@const now = current(entry.prices)}
      {@const key = `mix:${entry.mix_design.id}`}
      <li style="--i: {i}">
        <div class="a-card rate">
          <span class="a-card__rail" class:a-card__rail--muted={now === null} aria-hidden="true"></span>
          <div class="rate__body">
            {@render rateHead(
              entry.mix_design.mix_code,
              entry.mix_design.project_name,
              now ? money(now.sell_per_m3) : null,
              now ? `per m³, from ${now.effective_from}` : '',
              'no price yet'
            )}
            <div class="acts">
              <button class="btn btn--sm" type="button" onclick={() => openChange(key, entry.mix_design.mix_code)}>{now ? 'Change' : 'Set price'}</button>
              {#if entry.prices.length > 0}
                {@render histToggle(expandedKey(key), entry.prices.length)}
              {/if}
            </div>
            {#if openForm === key}{@render rateForm(entry.mix_design.id, false, false, key)}{/if}
            {@render removeAsk(key, entry.mix_design.mix_code, entry.mix_design.id)}
            {@render history(entry.prices, ((r: MixDesignPrice) => money(r.sell_per_m3)) as never, expandedKey(key))}
          </div>
        </div>
      </li>
    {/each}
  </ul>
{/if}

{#snippet removedFold()}
  {#if removedGroups.length + tabDismissed.length > 0}
    <!--
      Two kinds of removed, one list, because the difference is bookkeeping
      and not something to make anyone read about: an item that HAS rates
      keeps them and still prices on any day it appears, and a name that
      never had one simply stops being asked about. Neither touches history,
      and both come back from here. One fold serves all six tabs.
    -->
    <button class="fold fold--block" type="button" aria-expanded={showRemoved} onclick={() => (showRemoved = !showRemoved)}>
      <svg viewBox="0 0 24 24" aria-hidden="true" class:open={showRemoved}><path d="m6 9 6 6 6-6" /></svg>
      {showRemoved ? 'Hide removed' : 'Show removed'} ({removedGroups.length + tabDismissed.length})
    </button>
    {#if showRemoved}
      <ul class="a-list">
        {#each tabDismissed as name, i (name)}
          <li style="--i: {i}">
            <div class="a-card rate rate--removed">
              <span class="a-card__rail a-card__rail--muted" aria-hidden="true"></span>
              <div class="rate__body">
                <div class="rate__head">
                  <div class="rate__name">{name}</div>
                  <div class="rate__now">
                    <span class="rate__none">Never had a rate — not asked about</span>
                  </div>
                </div>
                <div class="acts">
                  <button class="btn btn--sm" type="button" onclick={() => void setDismissed(name, false)}>Restore</button>
                </div>
              </div>
            </div>
          </li>
        {/each}
        {#each removedGroups as group, i (group.key)}
          <li style="--i: {i}">
            <div class="a-card rate rate--removed">
              <span class="a-card__rail a-card__rail--muted" aria-hidden="true"></span>
              <div class="rate__body">
                <div class="rate__head">
                  <div class="rate__name">{group.label}</div>
                  <div class="rate__now">
                    <span class="rate__none">
                      {group.rows.length} rate row{group.rows.length === 1 ? '' : 's'} kept — still priced on past days
                    </span>
                  </div>
                </div>
                <div class="acts">
                  <button
                    class="btn btn--sm"
                    type="button"
                    onclick={() => void setArchived(group.label, false, { mixDesignId: group.mixDesignId, siteId: group.siteId })}
                  >
                    Restore
                  </button>
                  {#if group.rows.length > 0}
                    {@render histToggle(expandedKey(group.key), group.rows.length)}
                  {/if}
                </div>
                {@render history(group.rows, removedFigureOf as never, expandedKey(group.key))}
              </div>
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  {/if}
{/snippet}
{@render removedFold()}

<style>
  .note {
    margin: 0 0 0.7rem;
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  /* The "(optional)" tag on the equipment week/month labels — quieter than the
     label it sits beside, so a required box still reads as the primary one. */
  .opt {
    font-weight: 400;
    color: var(--ink-faint);
  }

  /* The Operators/Workers warning: tinted like the rate-gap cards, because it
     is the same kind of thing — a caution, not a fault. */
  .note--warn {
    margin: 0 0 0.7rem;
    padding: 0.55rem 0.75rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--warn);
    background: var(--warn-tint);
    font-size: 0.86rem;
    color: var(--ink);
  }
  .note--warn strong {
    display: block;
    margin-bottom: 0.15rem;
  }
  .link {
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-weight: 700;
    color: var(--brand);
    text-decoration: underline;
    cursor: pointer;
    touch-action: manipulation;
  }
  .link:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* Eight tabs will not fit across a phone, so the strip scrolls rather than
     shrinking every label past reading. */
  .tabs {
    display: flex;
    gap: 0.35rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 calc(-1 * var(--pad)) 0.9rem;
    padding: 0.15rem var(--pad) 0.45rem;
    scrollbar-width: thin;
  }
  .tab {
    flex: 0 0 auto;
    min-height: var(--tap);
    padding: 0 0.9rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    font: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
    white-space: nowrap;
  }
  .tab[aria-pressed='true'] {
    background: var(--brand);
    border-color: var(--brand-dark);
    color: #fff;
  }
  .tab:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  /* ---- one card per priced thing ---------------------------------------- */
  /* The card is not a link, so it must not lift on hover and promise a click
     it will not honour. */
  .rate {
    grid-template-columns: auto 1fr;
    align-items: stretch;
  }
  .rate:hover {
    transform: none;
    box-shadow: var(--shadow);
  }
  .rate:active {
    transform: none;
  }
  .rate--removed {
    background: var(--bg-sunken);
    box-shadow: none;
  }

  .rate__body {
    display: grid;
    gap: 0.5rem;
    min-width: 0;
    padding: 0.1rem 0;
  }
  .rate__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.2rem 0.8rem;
  }
  .rate__name {
    font-size: 1.08rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .rate__sub {
    display: block;
    font-size: 0.78rem;
    font-weight: 400;
    color: var(--ink-faint);
  }
  .rate__now {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    flex-wrap: wrap;
  }
  .rate__now b {
    font-size: 1.08rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .rate__now small {
    font-size: 0.76rem;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
  }
  .rate__none {
    font-size: 0.82rem;
    color: var(--ink-faint);
  }

  /* Tinted, not alarmed: a screenful of these is a to-do list, not a fault
     report, and red would say the app is broken when it is only incomplete. */
  .rate--gap {
    background: var(--warn-tint);
  }

  .rate__seen {
    margin: 0.15rem 0 0.4rem;
    font-size: 0.82rem;
    color: var(--ink-faint);
  }

  .tab__count {
    margin-left: 0.35rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    background: var(--warn);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .acts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
  }

  /* A fold: the chevron that opens history, and the one that shows removed
     workers. */
  .fold {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    min-height: var(--tap);
    padding: 0 0.4rem;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--brand);
    cursor: pointer;
    touch-action: manipulation;
  }
  .fold svg {
    width: 1.05rem;
    height: 1.05rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition: transform 200ms ease-out;
  }
  .fold svg.open {
    transform: rotate(180deg);
  }
  .fold:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
    border-radius: var(--radius);
  }
  .fold--block {
    margin-top: 0.3rem;
    color: var(--ink-soft);
  }

  /* ---- history ---------------------------------------------------------- */
  .hist {
    list-style: none;
    margin: 0;
    padding: 0 0 0 0.6rem;
    border-left: 3px solid var(--line-soft);
    display: grid;
    gap: 0.35rem;
    font-size: 0.88rem;
  }
  .hist__row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem 0.7rem;
    align-items: center;
    min-height: var(--tap);
  }
  .hist__when {
    color: var(--ink-soft);
  }
  .hist__figure {
    font-weight: 700;
  }
  .hist__note {
    flex: 1 1 8rem;
    min-width: 0;
    color: var(--ink-faint);
    overflow-wrap: anywhere;
  }

  /* Inline banner: this app has no modals. */
  .ask {
    display: grid;
    gap: 0.5rem;
    padding: 0.6rem 0.7rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--brand);
    background: var(--brand-tint);
  }
  .ask--danger {
    border-left-color: var(--hold);
    background: var(--hold-tint);
  }
  .ask p {
    margin: 0;
    font-size: 0.88rem;
    font-weight: 600;
  }
  .ask__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  /* ---- the one inline form ---------------------------------------------- */
  .rateform {
    display: grid;
    gap: 0.5rem;
    padding: 0.7rem 0.75rem 0.8rem;
    border-radius: var(--radius);
    border-left: 4px solid var(--brand);
    background: var(--brand-tint);
    max-width: 32rem;
  }
  .rateform__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* A tickable pill per material. Three of them wrap rather than shrink, so
     each keeps a full thumb's worth of target on the narrowest phone. */
  .checks {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }
  .check {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: var(--tap);
    padding: 0 0.85rem 0 0.6rem;
    border: 1.5px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--ink-soft);
    cursor: pointer;
    touch-action: manipulation;
  }
  .check input {
    width: 1.15rem;
    height: 1.15rem;
    margin: 0;
    accent-color: var(--brand);
  }
  .check:has(input:checked) {
    border-color: var(--brand);
    color: var(--brand);
  }
  .check:focus-within {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  .addpanel {
    background: var(--paper);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    padding: 0.75rem;
    margin-top: 0.6rem;
  }
  .add {
    margin-top: 0.7rem;
  }

  .empty {
    display: grid;
    gap: 0.4rem;
    justify-items: start;
    background: var(--paper);
    border-radius: var(--radius-lg);
    padding: 1.2rem;
    box-shadow: var(--shadow);
  }

  @media (prefers-reduced-motion: reduce) {
    .fold svg { transition: none; }
  }
</style>
